import React, { useEffect, useState, useCallback } from "react";
import AppLayout from "@/components/AppLayout";
import { api } from "@/lib/api";
import { Plus, Trash2, Save, Loader2, MessageSquare, Pencil, X } from "lucide-react";
import { toast } from "sonner";

export default function WaTemplatesPage() {
  const [items, setItems] = useState([]);
  const [editing, setEditing] = useState(null);
  const [creating, setCreating] = useState(null);
  const [loading, setLoading] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await api.get("/wa-templates");
      setItems(r.data.items || []);
    } finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const save = async (t, isNew) => {
    if (!t.name?.trim() || !t.text?.trim()) { toast.error("Nome e testo obbligatori"); return; }
    try {
      if (isNew) await api.post("/wa-templates", { name: t.name, text: t.text });
      else await api.patch(`/wa-templates/${t.id}`, { name: t.name, text: t.text });
      toast.success("Template salvato");
      setEditing(null); setCreating(null); load();
    } catch { toast.error("Errore"); }
  };
  const del = async (id) => {
    if (!window.confirm("Eliminare il template?")) return;
    await api.delete(`/wa-templates/${id}`);
    toast.success("Eliminato"); load();
  };
  const seed = async () => {
    const r = await api.post("/wa-templates/seed-defaults");
    if (r.data.created > 0) toast.success(`${r.data.created} template di esempio creati`);
    load();
  };

  return (
    <AppLayout
      title="Template WhatsApp"
      subtitle="Messaggi rapidi"
      actions={
        <div className="flex gap-2">
          {items.length === 0 && <button onClick={seed} data-testid="btn-wa-seed" className="px-3 py-2 text-sm border border-slate-300 rounded-[3px] hover:bg-slate-50">Esempi</button>}
          <button onClick={() => setCreating({ name: "", text: "" })} data-testid="btn-wa-new" className="text-white text-sm px-4 py-2 rounded-[3px] flex items-center gap-2" style={{ background: "var(--navy)" }} onMouseEnter={(e)=>e.currentTarget.style.background="var(--navy-3)"} onMouseLeave={(e)=>e.currentTarget.style.background="var(--navy)"} ><Plus className="w-4 h-4" /> Nuovo</button>
        </div>
      }
    >
      <div className="bg-white border border-slate-200 rounded-[3px] shadow-[0_1px_3px_rgba(10,25,47,0.05)] p-5">
        <div className="text-xs text-slate-500 mb-4">
          Usa <code className="font-mono bg-slate-100 px-1 rounded">{"{nome}"}</code>, <code >{"{cognome}"}</code>, <code >{"{agent}"}</code> nel testo per i segnaposto.
        </div>

        {creating && <TemplateEditor t={creating} onChange={setCreating} onSave={() => save(creating, true)} onCancel={() => setCreating(null)} />}

        {loading ? (
          <div className="py-8 text-center inline text-slate-400"><Loader2 className="w-5 h-5 animate-spin" /></div>
        ) : items.length === 0 && !creating ? (
          <div className="py-12 text-center text-sm text-slate-500">Nessun template. Crealo o usa gli esempi.</div>
        ) : (
          <div className="space-y-3">
            {items.map((t) => editing?.id === t.id
              ? <TemplateEditor key={t.id} t={editing} onChange={setEditing} onSave={() => save(editing, false)} onCancel={() => setEditing(null)} />
              : (
                <div key={t.id} className="border border-slate-200 rounded-[3px] p-4 hover:border-slate-300 transition" data-testid={`tpl-${t.id}`}>
                  <div className="flex items-start justify-between gap-3">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-1">
                        <MessageSquare className="w-4 h-4 text-emerald-600" />
                        <span className="font-medium text-slate-900">{t.name}</span>
                      </div>
                      <div className="text-sm text-slate-700 whitespace-pre-wrap">{t.text}</div>
                    </div>
                    <div className="flex items-center gap-1 shrink-0">
                      <button onClick={() => setEditing({ ...t })} className="p-1.5 text-slate-600 hover:bg-slate-100 rounded-[3px]" title="Modifica"><Pencil className="w-4 h-4" /></button>
                      <button onClick={() => del(t.id)} className="p-1.5 text-rose-600 hover:bg-rose-50 rounded-[3px]" title="Elimina"><Trash2 className="w-4 h-4" /></button>
                    </div>
                  </div>
                </div>
              )
            )}
          </div>
        )}
      </div>
    </AppLayout>
  );
}

function TemplateEditor({ t, onChange, onSave, onCancel }) {
  return (
    <div className="border border-amber-300 bg-amber-50/30 rounded-[3px] p-4 mb-3">
      <div className="grid gap-3">
        <input
          value={t.name}
          onChange={(e) => onChange({ ...t, name: e.target.value })}
          placeholder="Nome del template"
          data-testid="tpl-name"
          className="w-full px-3 py-2 text-sm border border-slate-300 rounded-[3px] focus:border-[#0A192F] focus:ring-2 focus:ring-slate-900/8"
        />
        <textarea
          rows={5}
          value={t.text}
          onChange={(e) => onChange({ ...t, text: e.target.value })}
          placeholder="Testo del messaggio (usa {nome}, {cognome}, {agent})"
          data-testid="tpl-text"
          className="w-full px-3 py-2 text-sm border border-slate-300 rounded-[3px] focus:border-[#0A192F] focus:ring-2 focus:ring-slate-900/8 resize-y font-sans"
        />
        <div className="flex items-center justify-end gap-2">
          <button type="button" onClick={onCancel} className="px-3 py-1.5 text-sm border border-slate-300 rounded-[3px] hover:bg-white text-slate-700 inline mr-1"><X className="w-4 h-4" /> Annulla</button>
          <button type="button" onClick={onSave} data-testid="tpl-save" className="px-3 py-1.5 text-sm text-white rounded-[3px] flex items-center gap-1.5" style={{ background: "var(--navy)" }} onMouseEnter={(e)=>e.currentTarget.style.background="var(--navy-3)"} onMouseLeave={(e)=>e.currentTarget.style.background="var(--navy)"} ><Save className="w-4 h-4" /> Salva</button>
        </div>
      </div>
    </div>
  );
}

// Helper used by ClientDetailPage to substitute placeholders
export function renderTemplate(text, ctx = {}) {
  if (!text) return "";
  return text
    .replace(/\{nome\}/g, ctx.nome || "")
    .replace(/\{cognome\}/g, ctx.cognome || "")
    .replace(/\{agent\}/g, ctx.agent || "");
}
